/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  MenuItem,
  Scrollbar,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import customerConfig from 'src/config';
import { useActiveMenuStyle } from '../Policy/style';
import ServiceAccount from './ServiceAccount';
import OrganizationManagement from './OrgManagement';
import ApiToken from './ApiToken';
import { isInUIC } from '@Utils/common';
import Rbac from './Rbac';

import License from './License';
import SingleSignOn from './SingleSignOn';
import { RolesStoreProvider } from '@Models/useRolesModel';
import { LocalStoreProvider } from '@Models/useLocalAccountsModel';
import { RoleDefinitionProvider } from '@Models/rbac/useRoleDefinition';
import { RoleUsersStoreProvider } from '@Models/rbac/useRoleUsers';
import { PermissionNameEnum } from '@Types/permissions';
import PrivateRoute from '@Components/Common/PrivateRouter';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useLayoutStore } from '@Models/useLayoutModel';
import { AddUser } from './Rbac/Administrators/AddUser';
import { AddRole } from './Rbac/Roles/AddRole';
import { EmailReporting } from './EmailReporting';
import AddInManagement from './AddInManagement';

const Administrator = () => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [selectedKey, setSelectedKey] = React.useState<string>();
  const navigate = useNavigate();
  const activeMenuStyle = useActiveMenuStyle();
  const currentLocation = useLocation();
  const { managementScopeIds } = usePermissionStore();
  const { layoutHeight } = useLayoutStore();

  useEffect(() => {
    const path = currentLocation.pathname.split('/');
    if (path[2]) {
      setSelectedKey(`${path[2]}`);
    }
    console.log('currentLocation', currentLocation, path);
  }, []);

  const handelClick = (url: string) => {
    setSelectedKey(url);
    //do not add handleRedirectClick here, it will cause the page to refresh
    if (isInUIC()) {
      if (window.top) {
        const address = new URL(
          `${window.top.location.origin}/#/app/email-and-collaboration/administrator/${url}`
        );
        window.top.history.replaceState({}, '', address);
      }
    }
    navigate(url);
  };

  const subMenuItems = [
    {
      url: 'organization',
      element: <OrganizationManagement />,
      name: t('side.group.administrator.orgManagement'),
      show: customerConfig.ui.administrator.featureShow.organization,
      shouldBeAllScope: false,
    },
    {
      url: 'service-account',
      element: <ServiceAccount />,
      name: t('side.group.administrator.serviceAccount'),
      show: customerConfig.ui.administrator.featureShow.serviceAccount,
      shouldBeAllScope: false,
    },
    {
      url: 'user-management',
      element: <Rbac />,
      name: t('side.group.administrator.role'),
      show: customerConfig.ui.administrator.featureShow.userRole && !isInUIC(),
      shouldBeAllScope: true,
    },
    {
      url: 'license',
      element: <License />,
      name: t('side.group.administrator.licenseInformation'),
      show: customerConfig.ui.administrator.featureShow.license && !isInUIC(),
      shouldBeAllScope: false,
    },
    {
      url: 'single-sign-on',
      element: <SingleSignOn />,
      name: t('side.group.administrator.singleSignOnSettings'),
      show:
        customerConfig.ui.administrator.featureShow.SingleSignOn && !isInUIC(),
      shouldBeAllScope: true,
    },
    {
      url: 'api-token',
      element: <ApiToken />,
      name: t('side.group.administrator.integrationAPITokens'),
      show: customerConfig.ui.administrator.featureShow.apiToken,
      shouldBeAllScope: false,
    },
    {
      url: 'euq',
      element: <AddInManagement />,
      name: t('side.group.administrator.addInManagement'),
      show: customerConfig.ui.administrator.featureShow.euq,
      shouldBeAllScope: false,
    },
    {
      url: 'email-reporting',
      element: <EmailReporting />,
      name: t('administration.addIn.emailReporting.title'),
      show: customerConfig.ui.administrator.featureShow.euq,
      shouldBeAllScope: false,
    },
  ];

  const rbacSubMenuItems = [
    {
      url: 'add-role',
      element: <AddRole />,
      name: t('side.group.administrator.addRole'),
    },
    {
      url: 'edit-role/:id',
      element: <AddRole />,
      name: t('side.group.administrator.addRole'),
    },
    {
      url: 'add-role-without-target',
      element: <AddRole />,
      name: t('side.group.administrator.addRoleWithoutTarget'),
    },
    {
      url: 'add-user',
      element: <AddUser />,
      name: t('side.group.administrator.addUser'),
    },
    {
      url: 'edit-user/:id',
      element: <AddUser />,
      name: t('side.group.administrator.addUser'),
    },
  ];

  return (
    <Box bg={colorStyle.background.primary}>
      <Flex flexDirection="row" height={layoutHeight} maxHeight={layoutHeight}>
        <FlexItem flex="none">
          <Scrollbar maxHeight={layoutHeight} overflow="scroll">
            <Flex
              width="270px"
              borderRight="1px solid"
              height={layoutHeight}
              borderRightColor={colorStyle.background.selected}
              flexDirection="column"
            >
              <FlexItem>
                {subMenuItems.map(
                  (item) =>
                    item.show &&
                    (item.shouldBeAllScope
                      ? managementScopeIds === 'all'
                      : true) && (
                      <MenuItem
                        {...(selectedKey === item.url ? activeMenuStyle : {})}
                        key={item.url}
                        onClick={() => handelClick(item.url)}
                      >
                        <Text pl={12}>{item.name}</Text>
                      </MenuItem>
                    )
                )}
              </FlexItem>
            </Flex>
          </Scrollbar>
        </FlexItem>
        <Flex
          flex="auto"
          flexDirection="column"
          height="100%"
          width={'calc(100vh - 270px - 64px)'}
        >
          <LocalStoreProvider>
            <RolesStoreProvider>
              <RoleDefinitionProvider>
                <RoleUsersStoreProvider>
                  <Routes>
                    <Route
                      path={`/`}
                      element={
                        <PrivateRoute
                          readPermissionName={[
                            PermissionNameEnum.administrator,
                          ]}
                          shouldBeAllScope={false}
                        >
                          <OrganizationManagement />
                        </PrivateRoute>
                      }
                      key={'/'}
                    ></Route>
                    {customerConfig.ui.administrator.featureShow.userRole &&
                      !isInUIC() &&
                      rbacSubMenuItems.map(
                        (item) =>
                          customerConfig.ui.administrator.featureShow
                            .userRole && (
                            <Route
                              key={item.url}
                              path={`/user-management/${item.url}`}
                              element={
                                item.element && (
                                  <PrivateRoute
                                    readPermissionName={[
                                      PermissionNameEnum.administrator,
                                    ]}
                                    shouldBeAllScope={true}
                                  >
                                    {item.element}
                                  </PrivateRoute>
                                )
                              }
                            />
                          )
                      )}
                    {subMenuItems.map(
                      (item) =>
                        item.show && (
                          <Route
                            key={item.url}
                            path={`/${item.url}`}
                            element={
                              item.element && (
                                <PrivateRoute
                                  readPermissionName={[
                                    PermissionNameEnum.administrator,
                                  ]}
                                  shouldBeAllScope={item.shouldBeAllScope}
                                >
                                  {item.element}
                                </PrivateRoute>
                              )
                            }
                          />
                        )
                    )}
                  </Routes>
                </RoleUsersStoreProvider>
              </RoleDefinitionProvider>
            </RolesStoreProvider>
          </LocalStoreProvider>
        </Flex>
      </Flex>
    </Box>
  );
};
export default Administrator;

//backgroundColor={colorStyle.background.primary}
const FlexItem = (props: any) => <Box {...props} />;
